import React from "react";
import { Info, RefreshCw } from "lucide-react";
import I18nCoverageBar from "./I18nCoverageBar";

/**
 * TranslationSummaryWidget — QUESTIONNAIRE-2.0-A-S1.
 *
 * Sticky table showing per-language i18n coverage. Pure read.
 *
 * Out of scope for S1:
 *   - Status flip buttons.
 *   - Bulk operations (mark all reviewed).
 *   - Initialize scaffold endpoint trigger.
 * Those land in S2 / S3 / S5 of the analysis doc.
 */
const TranslationSummaryWidget = ({ summary, loading, error, onReload, onJumpToLang, highlightedLangs }) => {
  const highlightSet = highlightedLangs instanceof Set ? highlightedLangs : new Set(highlightedLangs || []);
  return (
    <div
      data-testid="admin-q-translation-summary"
      className="rounded-md border border-slate-200 bg-white"
    >
      <header className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Info className="w-4 h-4 text-sky-600" /> Translation coverage
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          {summary?.translatable_units_per_language ? (
            <span data-testid="admin-q-translation-summary-units">
              {summary.translatable_units_per_language} translatable units / language
            </span>
          ) : null}
          <button
            type="button"
            data-testid="admin-q-translation-summary-reload"
            onClick={onReload}
            className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 hover:bg-slate-50"
          >
            <RefreshCw className="w-3 h-3" /> Reload
          </button>
        </div>
      </header>

      {loading && (
        <p data-testid="admin-q-translation-summary-loading" className="p-3 text-xs text-slate-500">
          loading…
        </p>
      )}
      {error && (
        <p data-testid="admin-q-translation-summary-error" className="p-3 text-xs text-rose-700">
          {error.status || ""} {error.detail || "Failed to load translation summary"}
        </p>
      )}
      {!loading && !error && summary && (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-slate-50 text-[9px] font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Lang</th>
                <th className="px-2 py-1.5 text-left">Dir</th>
                <th className="px-2 py-1.5 text-right">Total</th>
                <th className="px-2 py-1.5 text-right">Filled</th>
                <th className="px-2 py-1.5 text-right">Missing</th>
                <th className="px-2 py-1.5 text-right">Draft</th>
                <th className="px-2 py-1.5 text-right">Pending</th>
                <th className="px-2 py-1.5 text-right">Reviewed</th>
                <th className="px-2 py-1.5 text-right">Needs upd.</th>
                <th className="px-2 py-1.5 text-right">Coverage</th>
                <th className="px-2 py-1.5 text-left">Bar</th>
                <th className="px-2 py-1.5 text-left">Scaffold</th>
              </tr>
            </thead>
            <tbody>
              {summary.languages.map((L) => {
                const testId = `admin-q-translation-summary-row-${L.language_code}`;
                const isHighlighted = highlightSet.has(L.language_code);
                return (
                  <tr
                    key={L.language_code}
                    data-testid={testId}
                    data-cascade-highlight={isHighlighted ? "true" : "false"}
                    className={`border-t border-slate-100 ${isHighlighted ? "bg-rose-50/70" : "hover:bg-slate-50"}`}
                  >
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        data-testid={`${testId}-jump`}
                        onClick={() => onJumpToLang?.(L.language_code)}
                        className="font-mono font-semibold uppercase text-sky-700 hover:underline"
                      >
                        {L.language_code}
                      </button>
                      <span className="ml-1 text-slate-500">{L.language_name_native}</span>
                      {L.is_base && (
                        <span className="ml-1 rounded bg-emerald-100 px-1 text-[9px] uppercase tracking-wide text-emerald-800">base</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 uppercase text-slate-600">{L.direction}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{L.total_keys}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{L.filled_keys}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{L.missing_keys}</td>
                    <td className="px-2 py-1.5 text-right text-slate-500" data-testid={`${testId}-draft`}>{L.draft_count}</td>
                    <td className="px-2 py-1.5 text-right text-amber-700">{L.pending_review_count}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-700" data-testid={`${testId}-reviewed`}>{L.reviewed_count}</td>
                    <td className="px-2 py-1.5 text-right text-rose-700">
                      {L.needs_update_count}
                      {isHighlighted && L.needs_update_count > 0 && (
                        <span
                          data-testid={`${testId}-cascade-badge`}
                          className="ml-1 inline-block rounded bg-rose-600 px-1 text-[9px] font-semibold uppercase text-white align-middle"
                          title="Recent cascade marked translations as needs_update"
                        >
                          recent
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-slate-800" data-testid={`${testId}-coverage`}>{L.completion_percentage}%</td>
                    <td className="px-2 py-1.5 w-24"><I18nCoverageBar lang={L} compact /></td>
                    <td className="px-2 py-1.5">
                      {L.is_base ? (
                        <span className="text-[9px] uppercase tracking-wide text-emerald-700">implicit</span>
                      ) : L.has_scaffold ? (
                        <span className="text-[9px] uppercase tracking-wide text-sky-700">scaffolded</span>
                      ) : (
                        <span data-testid={`${testId}-no-scaffold`} className="text-[9px] uppercase tracking-wide text-rose-700">no scaffold</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && summary && !summary.has_scaffold && (
        <p
          data-testid="admin-q-translation-summary-no-scaffold-banner"
          className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
        >
          This questionnaire has no i18n scaffold yet — non-base translations are inert until an admin
          initializes the 15-language scaffold (out of scope for S1; coming in S3).
        </p>
      )}
    </div>
  );
};

export default TranslationSummaryWidget;
